<template>
	<div
		:class="{
			'projects-list': true,
			'projects-list--empty': props.isCollapsed,
		}"
	>
		<template v-if="!props.isCollapsed">
			<div class="projects-list__filters">
				<div class="projects-list__filters-row projects-list__filters-row--header">
					<app-tooltip :text="createProjectTitle">
						<template #activator>
							<app-icon
								class="projects-list__filters-btn"
								name="plus"
								width="20px"
								color="#979797"
								:hover-color="createProjectHoverColor"
								:cursor="createProjectCursor"
								@click="onCreateProject"
							/>
						</template>
					</app-tooltip>
					<div v-if="!isEntityPage" class="jobs-panel-search__header-inputs">
						<div class="jobs-panel-search__header-inputs-item">
							<app-input
								v-model="filter"
								:placeholder="$t('search_label')"
								show-search-button
								@press-enter="doSearch"
								@search="doSearch"
							/>
						</div>
						<div class="jobs-panel-search__header-inputs-item">
							<app-select
								v-model="selectedCategory"
								:placeholder="$t('select_category')"
								:items="optionsDisplay"
								:min-dropdown-width="272"
								searchable
							/>
						</div>
					</div>
					<div
						v-else
						class="projects-list__filters-input"
					>
						<app-input
							v-model="filter"
							:placeholder="$t('search_label')"
							show-search-button
							@press-enter="doSearch"
							@search="doSearch"
						/>
					</div>
					<app-resizable-dropdown
						ref="$dropdownRef"
						position="right"
						:close-by-click-outside="true"
						:max-width="640"
						:max-height="436"
						:mobile-title="$t('choose_tags')"
						name="$dropdownRefClientsTags"
					>
						<template #activator>
							<app-tooltip :text="$t('ui_tag_filter')">
								<template #activator>
									<app-icon
										name="hash"
										width="20px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>

						<app-tag-selector
							v-model="selectedTags"
							:entity-type="TagEntityType.Project"
							mode="filter"
							allow-create
							allow-select-group
							@close="$dropdownRef?.close()"
						/>
					</app-resizable-dropdown>
					<app-resizable-dropdown
						v-if="isEntityPage"
						ref="$clientFilterDropdownRef"
						position="right"
						:close-by-click-outside="true"
						ignore-click-outside="[data-client-filter-chip]"
						:max-width="640"
						:max-height="436"
						:mobile-title="$t('choose_clients')"
						name="$dropdownProjectClientFilter"
						@close="focusFilterClient = null"
					>
						<template #activator>
							<app-tooltip :text="$t('clients_filter')">
								<template #activator>
									<app-icon
										name="filter"
										width="20px"
										color="#979797"
										hover-color="#499259"
										cursor="pointer"
									/>
								</template>
							</app-tooltip>
						</template>

						<project-client-filter-dropdown
							:focus-client="focusFilterClient"
							@close="$clientFilterDropdownRef?.close()"
							@change="doSearch"
						/>
					</app-resizable-dropdown>
				</div>
				<div
					v-if="isEntityPage"
					class="projects-list__filters-row projects-list__filters-row--inputs"
				>
					<div class="projects-list__filters-select">
						<app-select
							v-model="selectedCategory"
							:placeholder="$t('select_category')"
							:items="optionsDisplay"
							:min-dropdown-width="272"
							searchable
						/>
					</div>
				</div>
			</div>
			<div class="projects-list__stages">
				<template
					v-for="t in stageItemsForSelectedStage"
					:key="t.Id"
				>
					<app-tooltip
						v-if="t.Count || allSelectedStages.includes(t.Id)"
						:text="t.Name"
					>
						<template #activator>
							<div
								class="tag-chip"
								:style="chipStyle(t)"
								@click="toggleStage(t.Id)"
							>
								<div
									class="tag-chip__colored"
									:style="chipColoredStyle(t)"
								/>
								<div class="tag-chip__count">{{ t.Count }}</div>
							</div>
						</template>
					</app-tooltip>
				</template>
			</div>
			<div
				v-if="isEntityPage && (byBase || selectedClients.length)"
				class="projects-list__selected-clients"
			>
				<project-selected-client-item
					v-if="byBase"
					:client="byBaseItem"
					:role-editable="false"
					:removable="false"
					@open-filter="openClientFilterAt(byBaseItem)"
				/>
				<project-selected-client-item
					v-for="c in selectedClients"
					:key="`${c.Id}-${c.UseUnion}`"
					:client="c"
					@remove="removeClient(c.Id)"
					@change="doSearch"
					@open-filter="openClientFilterAt(c)"
				/>
			</div>
			<div
				v-if="selectedTags.length"
				class="projects-list__selected-tags"
			>
				<div
					v-for="tag in selectedTags"
					:key="tag.Id"
					class="projects-list__selected-tags-item"
				>
					<div class="projects-list__selected-tags-item-name">
						{{ tag.Name }}
					</div>
					<app-icon
						class="projects-list__selected-tags-item-delete"
						name="close-bold"
						size="12px"
						color="#D2D2D2"
						hover-color="#4D4D4D"
						cursor="pointer"
						@click.stop="dropTag(tag)"
					/>
				</div>
			</div>
			<div
				class="projects-list__filters-counter"
				:style="{ margin: props.isCollapsed ? 'auto 0' : '8px 0' }"
				@click="emit('on-toggle')"
				@mouseenter="isCounterHovered = true"
				@mouseleave="isCounterHovered = false"
			>
				{{ $t(hasAnyFilter ? 'projects_found' : 'projects_total') }}:&nbsp;
				<span class="projects-list__filters-counter-value">{{ projectsCount }}</span>
				<app-tooltip
					class="projects-list__filters-counter-tooltip"
					:text="$t(props.isSecondCollapsed ? 'ui_shrink' : 'ui_expand')"
				>
					<template #activator>
						<app-icon
							class="projects-list__filters-counter-icon"
							:name="props.isSecondCollapsed ? 'small-screen' : 'full-screen'"
							size="14px"
							:color="isCounterHovered ? '#438451' : '#979797'"
							hover-color="#438451"
							cursor="pointer"
						/>
					</template>
				</app-tooltip>
				<div
					v-if="hasAnyFilter"
					class="projects-list__filters-counter-drop-filters"
					@click.stop="dropFilters"
				>
					{{ $t('selector_reset') }}
				</div>
			</div>
			<div
				class="projects-list__body"
				ref="$projectsList"
				@scroll="onProjectsScroll"
			>
				<app-loader
					v-if="projectsLoading"
					:style="loaderStyle"
				/>
				<template
					v-for="(project, index) in projects"
					:key="project.Id"
				>
					<project-list-item :project="project" />
					<div
						v-if="index + 1 < projects.length"
						class="projects-list__body-divider"
					/>
				</template>
				<app-scroll-top-button
					:enabled="isScrollTopEnabled"
					@on-click="scrollToTop"
				/>
			</div>
		</template>
		<app-tooltip
			v-else
			class="projects-list__empty-tooltip"
			:text="$t('ui_expand')"
		>
			<template #activator>
				<div
					class="projects-list__empty"
					@click="emit('on-toggle')"
					@mouseenter="isCounterHovered = true"
					@mouseleave="isCounterHovered = false"
				>
					{{ $t(hasAnyFilter ? 'projects_found' : 'projects_total') }}:&nbsp;
					<span class="projects-list__empty-count">{{ projectsCount }}</span>
					<app-icon
						class="projects-list__empty-icon"
						name="full-screen"
						width="14px"
						:color="isCounterHovered ? '#438451' : '#979797'"
						hover-color="#438451"
					/>
				</div>
			</template>
		</app-tooltip>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	watch,
	onMounted,
	nextTick
} from "vue";
import { useRoute, useRouter } from "vue-router";
import {searchStore} from "@/store/search";
import { clientPageStore } from "@/store/client-page";
import { headerStore } from "@/store/header";
import { ProjectApi } from "@/ api/ProjectApi";
import ProjectListItem from "@/components/main/search-panel/ProjectListItem.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppSelect from "@/components/ui-kit/select/AppSelect.vue";
import ProjectClientFilterDropdown from "@/components/main/search-panel/dropdowns/ProjectClientFilterDropdown.vue";
import ProjectSelectedClientItem from "@/components/main/search-panel/ProjectSelectedClientItem.vue";
import AppScrollTopButton from "@/components/ui-kit/button/AppScrollTopButton.vue";
import { useScrollTopButton } from "@/composables/useScrollTopButton";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { TagEntityType } from "@/models/etc/types";
import { i18n } from "@/i18n";

const t = (key: string): string => String(i18n.global.t(key))

const emit = defineEmits<{
	(e: 'on-toggle'): void
}>()

const props = defineProps<{
	isCollapsed: boolean,
	isSecondCollapsed: boolean
}>();

const $dropdownRef = ref<typeof AppResizableDropdown | null>(null)
const $clientFilterDropdownRef = ref<typeof AppResizableDropdown | null>(null)
const $projectsList = ref<HTMLDivElement | null>(null)
const store = searchStore()
const clientPage = clientPageStore()
const header = headerStore()
const route = useRoute()
const router = useRouter()
const filter = ref("");
const selectedCategory = ref(-1);
const selectedTags = ref<TagWithFrequency[]>([])
const isCreatingProject = ref(false)
const initialLoadDone = ref(false)
const isCounterHovered = ref(false)

const isEntityPage = computed(() => route.query.page === 'client' || route.query.page === 'project')
const entityType = computed<'client' | 'project'>(() => route.query.page === 'project' ? 'project' : 'client')
const entityId = computed(() => Number(route.query.id) || 0)

const byBaseItem = computed(() => ({ Id: -1, Name: t('search_by_base'), Roles: [] }))

const focusFilterClient = ref<{ Id: number, UseUnion: boolean } | null>(null)

const openClientFilterAt = (client: { Id: number, UseUnion?: boolean }) => {
	focusFilterClient.value = { Id: client.Id, UseUnion: !!client.UseUnion }
	$clientFilterDropdownRef.value?.open()
}

const canCreateProject = computed(() => !!header.currentAccessGroup?.CanCreateProjects)
const createProjectTitle = computed(() =>
	t(canCreateProject.value ? 'project_create' : 'project_create_unavailable')
)
const createProjectHoverColor = computed(() =>
	canCreateProject.value ? '#499259' : '#979797'
)
const createProjectCursor = computed(() =>
	isCreatingProject.value ? 'wait' : (canCreateProject.value ? 'pointer' : 'not-allowed')
)

// Проматывает список проектов до открытого Проекта. Вызывается ТОЛЬКО при
// навигационной (первичной) загрузке — не при ручных поисках на той же странице.
const maybeScrollToOpenedProject = async () => {
	if (entityType.value !== 'project' || !entityId.value) {
		return
	}
	await nextTick()
	const container = $projectsList.value
	if (!container) {
		return
	}
	const el = container.querySelector(`[data-project-id="${entityId.value}"]`)
	if (!el) {
		return
	}
	const cRect = container.getBoundingClientRect()
	const eRect = el.getBoundingClientRect()
	container.scrollTop += eRect.top - cRect.top
}

onMounted(async () => {
	await store.getInitProjects()

	if (isEntityPage.value) {
		await store.loadClientFilterList(entityType.value, entityId.value)
		if (entityType.value === 'client') {
			const opened = clientPage.openedClient
			if (opened?.Id === entityId.value) {
				store.preselectClient({ Id: opened.Id, Name: opened.Name })
			}
		} else {
			store.preselectMainProjectClient()
		}
		await store.getFilteredProjects(filter.value)
		if (entityType.value === 'project') {
			await maybeScrollToOpenedProject()
		}
	}

	initialLoadDone.value = true

	// добор преселекта, если openedClient появился во время монтирования
	await tryPreselectOpenedClient()
})

const onProjectsScroll = () => {
	const el = $projectsList.value
	if (!el || projectsLoading.value) {
		return
	}
	if (el.scrollTop + el.clientHeight >= el.scrollHeight - 800) {
		store.getFilteredProjectsPartion(filter.value)
	}
}

const projects = computed(() => store.allProjects)
const projectsLoading = computed(() => store.projectsLoading || isCreatingProject.value)

const { isScrollTopEnabled, scrollToTop } = useScrollTopButton(
	$projectsList,
	'[data-project-id]',
	() => projects.value
)

const projectsCount = computed(() => store.projectCounter)
const projectsStagesList = computed(() => store.projectsStagesList)
const stageItemsForSelectedStage = computed(() => store.stageItemsForSelectedStage)
const allSelectedStages = computed(() => store.allSelectedStages)
const selectedClients = computed(() => store.selectedClients)
const byBase = computed(() => store.byBase)
// Фильтр по клиентам считается изменённым, пока он не равен «По базе»
const hasClientFilter = computed(() => isEntityPage.value && !byBase.value)

const hasAnyFilter = computed(() =>
	!!filter.value
	|| allSelectedStages.value.length > 0
	|| selectedTags.value.length > 0
	|| selectedCategory.value !== -1
	|| hasClientFilter.value
)

const optionsDisplay = computed(() => {
	if (!projectsStagesList.value.length) {
		return [{
			id: -1,
			label: t('stage_standard'),
			type: 'item'
		}]
	}

	return  projectsStagesList.value.map(stage => ({
		id: stage.Id,
		label: stage.Name,
		type: 'item'
	}))
})

const doSearch = async () => {
	$projectsList.value ? $projectsList.value.scrollTop = 0 : null
	await store.getFilteredProjects(filter.value);
}

function clearSearch() {
	filter.value = "";
}

async function toggleStage(stageId: number) {
	$projectsList.value ? $projectsList.value.scrollTop = 0 : null
	await store.setSelectedStage(stageId, filter.value)
}

const dropFilters = () => {
	filter.value = ""
	selectedCategory.value = -1
	selectedTags.value = []
	store.dropTags()
	if (isEntityPage.value) {
		store.setByBase(true)
	} else {
		store.clearClientFilter()
	}
	doSearch()
}

const dropTag = (tag: TagWithFrequency) => {
	selectedTags.value = selectedTags.value.filter(t => t.Id !== tag.Id)
}

const removeClient = (id: number) => {
	store.removeSelectedClient(id)
	doSearch()
}

// Преселект открытого клиента (страница Клиента). Делает поиск только если действительно выбрал.
const tryPreselectOpenedClient = async () => {
	if (route.query.page !== 'client' || store.byBase) {
		return
	}
	const opened = clientPage.openedClient
	if (!opened?.Id || opened.Id !== entityId.value) {
		return
	}
	if (store.selectedClients.some(c => c.Id === opened.Id)) {
		return
	}
	store.preselectClient({ Id: opened.Id, Name: opened.Name })
	await doSearch()
}

const onCreateProject = async () => {
	if (isCreatingProject.value) {
		return
	}
	if (!canCreateProject.value || header.currentAccessGroupId == null) {
		return
	}

	try {
		isCreatingProject.value = true
		const clientId = store.selectedClients[0]?.Id ?? 0
		const newId = await ProjectApi.createProject(clientId, header.currentAccessGroupId)
		if (newId) {
			store.clearClientFilter()
			await router.push({
				path: '/main',
				query: {
					page: 'project',
					id: String(newId),
				}
			})
		}
	} catch (e) {
		console.error(e)
	} finally {
		isCreatingProject.value = false
	}
}

// Загрузка списка фильтра при открытии Клиента/Проекта/Главной
watch(
	() => [route.query.page, route.query.id],
	async (_curr, [prevPage]) => {
		if (!initialLoadDone.value) return
		if (isEntityPage.value) {
			await store.loadClientFilterList(entityType.value, entityId.value)
			if (entityType.value === 'project') {
				store.preselectMainProjectClient()
				await doSearch()
				await maybeScrollToOpenedProject()
			} else {
				const opened = clientPage.openedClient
				if (opened?.Id === entityId.value) {
					store.preselectClient({ Id: opened.Id, Name: opened.Name })
					await doSearch()
				}
				// иначе преселект и поиск сделает Watcher B по готовности openedClient
			}
		} else if (prevPage === 'client' || prevPage === 'project') {
			store.clearClientFilter()
			await doSearch()
		}
	}
)

// Добивает преселект открытого клиента, если он загрузился позже списка
watch(
	() => clientPage.openedClient?.Id ?? null,
	async () => {
		if (!initialLoadDone.value || route.query.page !== 'client' || store.byBase) {
			return
		}
		const opened = clientPage.openedClient
		if (!opened?.Id || opened.Id !== entityId.value) {
			return
		}
		if (store.selectedClients.some(c => c.Id === opened.Id)) {
			return
		}
		store.preselectClient({ Id: opened.Id, Name: opened.Name })
		await doSearch()
	}
)

watch(selectedTags, (newTags) => {
	store.selectedTags = newTags.map(t => t.Id)
}, { deep: true })

const chipStyle = (t: any) => {
	const active = allSelectedStages.value.includes(t.Id);
	const base = {
		borderLeftColor: t.Color,
		backgroundColor: 'transparent'
	}
	if (active) {
		base.backgroundColor = hexToRgba(t.Color, 0.121);
	}

	return base;
}

const chipColoredStyle = (t: any) => {
	return {
		backgroundColor: t.Color
	}
}

function hexToRgba(hex: string, alpha: number) {
	const res = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	if (!res) return hex;
	const r = parseInt(res[1], 16);
	const g = parseInt(res[2], 16);
	const b = parseInt(res[3], 16);
	return `rgba(${r},${g},${b},${alpha})`;
}

watch(filter, (newValue, oldValue) => {
	if (!newValue && oldValue) {
		doSearch()
	}
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

watch(selectedCategory, () => {
	store.selectedStage = selectedCategory.value;
});
</script>

<style lang="scss" scoped>
.projects-list {
	display: flex;
	flex-direction: column;
	background-color: #fff;
	border-radius: 8px;
	height: 100%;

	&:hover :deep(.app-scroll-top--enabled) {
		opacity: 1;
		visibility: visible;
		pointer-events: auto;
	}

	:deep(.app-scroll-top) {
		margin-right: 4px;
	}

	&--empty {
		justify-content: center;
	}

	&__empty-tooltip,
	&__empty-tooltip :deep(.app-tooltip__activator) {
		display: block;
		width: 100%;
	}

	&__empty {
		display: flex;
		align-items: center;
		justify-content: center;
		color: #4D4D4D;
		font-size: 12px;
		line-height: 16px;
		cursor: pointer;

		&-count {
			font-size: 12px;
			line-height: 16px;
			font-weight: 500;
			margin-right: 2px;
		}

		&-icon {
			margin-top: -2px;
		}
	}

	&__filters {
		display: flex;
		flex-direction: column;
		padding: 8px;

		&-row {
			display: flex;
			align-items: center;
			color: #000;
			margin-bottom: 8px;

			&:last-child {
				margin-bottom: 0;
			}

			&--inputs {
				display: flex;
				gap: 4px;
				width: 100%;
				margin-bottom: 0;
			}

			&--header {
				gap: 4px;
			}

		}

		&-counter {
			display: flex;
			align-items: center;
			justify-content: center;
			position: relative;
			font-size: 12px;
			line-height: 16px;
			font-weight: 400;
			color: #4D4D4D;
			cursor: pointer;

			&-value {
				font-size: 12px;
				line-height: 16px;
				font-weight: 500;
				margin-right: 2px;
			}

			&-icon {
				margin-top: -2px;
			}

			&-tooltip,
			&-tooltip :deep(.app-tooltip__activator) {
				display: flex;
			}

			&-drop-filters {
				position: absolute;
				right: 8px;
				bottom: 0;
				font-size: 12px;
				line-height: 16px;
				color: #979797;
				cursor: pointer;

				&:hover {
					text-decoration: underline;
				}
			}
		}

		&-btn {
			color: #979797;

			&:hover {
				color: #499259;
			}
		}

		&-search {
			width: 100%;
		}

		&-input {
			flex: 1;
			min-width: 0;
		}

		&-select {
			flex: 1;
			min-width: 0;
		}
	}

	&__stages {
		display: flex;
		flex-wrap: wrap;
		gap: 4px;
		padding: 0 8px;
	}

	&__selected-tags {
		display: flex;
		flex-wrap: wrap;
		padding: 0 8px;
		margin-top: 8px;
		gap: 4px;

		&-item {
			display: flex;
			align-items: center;
			text-transform: lowercase;

			&-name {
				color: #979797;
				font-size: 14px;
				line-height: 18px;
			}
		}
	}

	&__selected-clients {
		display: flex;
		flex-direction: column;
		gap: 4px;
		max-height: 100px;
		overflow: auto;
		padding: 0 8px;
		margin-top: 8px;
	}

	&__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		position: relative;
		overflow: auto;
		padding: 8px 4px 0;
		border-top: 1px solid #F2F2F2;
	}

	&__body-divider {
		display: flex;
		width: 100%;
		border-top: 1px solid #F2F2F2;
		margin: 8px 0;
	}

	&::v-deep(.q-field__control) {
		min-height: 32px !important;
		height: 32px;
	}

	&::v-deep(.q-field__native) {
		min-height: 32px !important;
	}

	&::v-deep(.q-field__append) {
		height: 32px;
	}
}

.project-dropdown {
	border-radius: 8px;

	.q-field__control:after,
	.q-field__control:before {
		content: none;
	}
}

.dropdown-option {
	padding: 0;
}

.dropdown-divider {
	height: 0.5px;
	background: #000;
}

.tag-chip {
	display: inline-flex;
	align-items: stretch;
	position: relative;
	background: #fff;
	border-radius: 6px;
	color: #979797;
	cursor: pointer;
	user-select: none;
	box-shadow: 0 0 1px 0.5px #D2D2D2 inset;
	overflow: hidden;

	&__colored {
		width: 4px;
		flex-shrink: 0;
	}

	&__count {
		display: flex;
		justify-content: center;
		font-size: 12px;
		line-height: 18px;
		font-weight: 500;
		min-width: 24px;
		height: 22px;
		color: #4D4D4D;
		flex-grow: 1;
		padding: 2px 5px 2px 3px;
	}
}
</style>
